import { useEffect, useMemo, useRef, useState } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { useManifest } from "@/application/usecases/useManifest";
import { usePreloadLevel } from "@/application/usecases/usePreloadLevel";
import { computeHubCta } from "@/domain/learning/hub-cta";
import { computeLevelStatus } from "@/domain/learning/level-status";
import type { LevelSummary } from "@/domain/learning/model/level-summary";
import { backgroundAssetPath } from "@/infrastructure/assets/paths";
import { Button } from "@/presentation/components/Button";
import { Icon } from "@/presentation/components/icons/Icon";
import { HeaderZone, MainZone } from "@/presentation/components/layout/Zones";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { Skeleton } from "@/presentation/components/Skeleton";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import { SettingsPanel } from "@/presentation/features/settings/SettingsPanel";
import { LABELS, levelDisplayName } from "@/presentation/i18n/labels";
import type { Route } from "@/presentation/router/route";
import { LevelRow } from "./LevelRow";
import "./LevelSelect.css";

interface LevelSelectProps {
  readonly navigate: (route: Route) => void;
  /**
   * 携帯 / 1024px未満での表示（O-3。ui-design.md §7.8・§4.1）。
   * 稽古への導線（CTA・段の行）を無効化ではなく非表示にする（F-010）。既定はfalse。
   */
  readonly isMobileOrNarrow?: boolean;
}

function ctaLabel(cta: ReturnType<typeof computeHubCta>): { text: string; levelId: string | null } {
  if (cta.kind === "start") {
    return { text: "はじめる", levelId: null };
  }
  const name = levelDisplayName(cta.levelId) ?? cta.levelId;
  if (cta.kind === "continue") {
    return { text: `つづきから（${name}）`, levelId: cta.levelId };
  }
  return { text: `復習する（${name}）`, levelId: cta.levelId };
}

/** S-1 稽古の段（`/`）。学習者を最短で「次にやるべき1つ」へ送り出すハブ画面（N-001）。 */
export function LevelSelect({ navigate, isMobileOrNarrow = false }: LevelSelectProps) {
  const { status, levels, retry } = useManifest();
  const progress = useProgressStore((state) => state.progress);
  const preload = usePreloadLevel();
  const { openPopover } = useOverlay();
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useBgm("gate");

  const cta = useMemo(() => computeHubCta(levels ?? [], progress), [levels, progress]);
  const { text: ctaText, levelId: ctaLevelId } = ctaLabel(cta);

  useEffect(() => {
    ctaRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleGlobalKeyDown(event: KeyboardEvent) {
      // 帳（O-1）やキー一覧（O-5）が開いている間は、画面のキーは働かせない
      // （screen-flow.md §9.2。Escape はそれらを閉じる操作であり、画面の遷移ではない）。
      if (document.querySelector(".popover, .modal-panel")) {
        return;
      }
      if (event.key === "m") {
        navigate({ name: "stamps" });
      } else if (event.key === "Escape") {
        // 免状帳（S-3）の Esc と同じ作法で、一つ手前の画面（門）へ戻す（D-119）。
        navigate({ name: "title" });
      }
    }
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [navigate]);

  function goToLevel(levelId: string) {
    navigate({ name: "level", levelId });
  }

  function handleRowKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (!levels) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(index + 1, levels.length - 1);
      setFocusedRowIndex(next);
      rowRefs.current[next]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev = Math.max(index - 1, 0);
      setFocusedRowIndex(prev);
      rowRefs.current[prev]?.focus();
    } else if (event.key === "Enter") {
      const level = levels[index] as LevelSummary;
      goToLevel(level.id);
    }
  }

  return (
    <div className="level-select">
      {/* 門の背景は情景の装飾（ui-design.md §11.2）。読み上げからは隠す。 */}
      <img
        className="level-select__background"
        src={backgroundAssetPath("gate")}
        width={1920}
        height={1080}
        alt=""
        aria-hidden="true"
      />
      <div className="level-select__scrim" aria-hidden="true" />
      <HeaderZone className="level-select__header">
        {/* 門（S-0）へ戻る脱出口。稽古の段は門の次の間であり、行き止まりにしない（D-119）。 */}
        <button
          type="button"
          className="level-select__back"
          onClick={() => navigate({ name: "title" })}
        >
          <Icon name="back" decorative size={20} />
          <span>門へ</span>
        </button>
        <span className="z-header__actions">
          <button
            type="button"
            className="z-header__icon-btn"
            onClick={() => navigate({ name: "stamps" })}
            aria-label={LABELS.stampBook}
          >
            <Icon name="mokuroku" title={LABELS.stampBook} />
          </button>
          <button
            type="button"
            className="z-header__icon-btn"
            aria-label="設定"
            onClick={openPopover}
          >
            <Icon name="gear" title="設定" />
          </button>
        </span>
        <SettingsPanel />
      </HeaderZone>
      <MainZone className="level-select__main">
        {/*
          二段組（D-118）。左は「掲げ」の柱（何の道場か・次の一手）、右は「段の一覧」。
          縦に積むと掲げだけで画面の半分を使い、1024×768では段が2つしか見えなかった。
          左右に分けることで、段の一覧が最初の画面に収まり、行の横幅も締まって
          打鍵の札と状態の間の空白が消える。段を縦1列に並べる筋立て（D-103）は保つ。
        */}
        <div
          className={
            isMobileOrNarrow
              ? "level-select__layout level-select__layout--single"
              : "level-select__layout"
          }
        >
          <div className="level-select__aside">
            <div className="level-select__hero-panel">
              {/* ロゴ画像に既に「vim道場」の文字意匠が入っているため、見出しは画面の主旨を表す
                  「稽古の段」を表示し、「vim道場」は肩書き（kicker）に格下げして重複を解消する。 */}
              <Icon name="enso" decorative size={88} className="level-select__logo" />
              <p className="level-select__hero-kicker">vim道場</p>
              <h1 className="level-select__hero-title">稽古の段</h1>
              {!isMobileOrNarrow && (
                <Button
                  ref={ctaRef}
                  variant="primary"
                  keyHint="⏎"
                  className="level-select__cta"
                  onClick={() =>
                    ctaLevelId ? goToLevel(ctaLevelId) : levels?.[0] && goToLevel(levels[0].id)
                  }
                >
                  {ctaText}
                </Button>
              )}
            </div>

            {isMobileOrNarrow && (
              <p className="level-select__mobile-note">
                稽古はキーボードでの利用を前提にしています。ここでは話と免状帳を読めます。
              </p>
            )}
          </div>

          {!isMobileOrNarrow && (
            <div className="level-select__list">
              {status === "error" && (
                <div className="level-select__error" role="alert">
                  <p>教材の目次を読み込めませんでした</p>
                  <Button variant="secondary" onClick={retry}>
                    再試行
                  </Button>
                </div>
              )}

              {status === "loading" && (
                <div className="level-select__rows" aria-hidden="true">
                  {Array.from({ length: 7 }, (_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: スケルトンは中身を持たない固定表示
                    <Skeleton key={i} width="100%" height={84} />
                  ))}
                </div>
              )}

              {status === "loaded" && levels && (
                <div className="level-select__rows">
                  {levels.map((level, index) => {
                    const levelStatus = computeLevelStatus(level.id, level.drillCount, progress);
                    return (
                      <LevelRow
                        key={level.id}
                        ref={(el) => {
                          rowRefs.current[index] = el;
                        }}
                        level={level}
                        status={levelStatus}
                        tabIndex={index === focusedRowIndex ? 0 : -1}
                        onHoverPreload={preload}
                        onSelect={goToLevel}
                        onKeyDown={(event) => handleRowKeyDown(event, index)}
                        /*
                          段の行は上から順に立ち上がる（D-116）。ハブは遷移の計測（NFR-001）の
                          始点であって終点ではない（計測窓は行のクリック後に開く）ため、
                          ここでの入場アニメーションは計測に乗らない。
                        */
                        enterIndex={index}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </MainZone>
    </div>
  );
}
